import { useEffect, useState } from 'react';
import api, { assignmentAPI, vehicleAPI, driverAPI, lookupAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Button, Table, StatusBadge, Modal, Input, Select, Loading, Alert } from '../components/ui';

// ── Hidden-iframe printing (same approach as repair letters) ─────────────────
const printHTML = (html) => {
  const existing = document.getElementById('assign-print-frame');
  if (existing) existing.remove();
  const iframe = document.createElement('iframe');
  iframe.id = 'assign-print-frame';
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`<html><head><title>Vehicle Assignment Agreement</title>
    <style>
      body{font-family:Arial,sans-serif;font-size:13px;line-height:1.7;padding:50px;max-width:820px;margin:0 auto;}
      h2{text-align:center;margin:0 0 4px;text-transform:uppercase;letter-spacing:1px;}
      .sub{text-align:center;color:#555;margin-bottom:22px;}
      table{width:100%;border-collapse:collapse;margin-bottom:18px;}
      th{background:#f2f2f2;text-align:left;padding:6px 10px;border:1px solid #ccc;width:34%;}
      td{padding:6px 10px;border:1px solid #ccc;}
      .sec{font-weight:bold;background:#e8e4f3;padding:6px 10px;border:1px solid #ccc;}
      .terms{font-size:12px;margin:18px 0;}
      .terms li{margin-bottom:5px;}
      .sign{margin-top:50px;width:100%;}
      .sign td{border:0;padding-top:44px;}
      .line{border-top:1px solid #333;width:220px;padding-top:4px;font-size:12px;}
      @media print { @page { margin: 1.8cm; } }
    </style></head><body>${html}</body></html>`);
  doc.close();
  setTimeout(() => { iframe.contentWindow.focus(); iframe.contentWindow.print(); }, 400);
};

const row = (label, value) => value ? `<tr><th>${label}</th><td>${value}</td></tr>` : '';

const buildAgreement = (a, branches) => {
  const v = a.vehicle || {};
  const branch = branches.find(b => b.id === a.branch_id);
  const isOfficer = a.assign_type === 'officer';
  const driverName = a.driver?.user?.name;
  return `
    <h2>Vehicle Assignment Agreement</h2>
    <div class="sub">${branch?.name || a.department || ''}</div>

    <div class="sec">Vehicle Details</div>
    <table>
      ${row('Vehicle No.', v.reg_number)}
      ${row('Make', v.brand)}
      ${row('Model', v.model)}
      ${row('Engine No.', v.engine_number)}
      ${row('Chassis No.', v.chassis_number)}
      ${row('Fuel Type', v.fuel_type)}
      ${row('YOM', v.year)}
    </table>

    <div class="sec">Assignment Details — ${isOfficer ? 'Officer' : 'Pool Vehicle'}</div>
    <table>
      ${isOfficer ? `
        ${row('Name', [a.officer_title, a.officer_name].filter(Boolean).join(' '))}
        ${row('Designation', a.officer_designation)}
        ${row('Company', a.officer_company)}
        ${row('Employee No.', a.officer_employee_no)}
        ${row('NIC', a.officer_nic)}
        ${row('Address', a.officer_address)}
      ` : `
        ${row('Responsible Person', a.responsible_person)}
        ${row("Responsible Person's Contact No.", a.responsible_phone)}
        ${row('Driver', driverName || 'N/A')}
        ${row("Driver's Contact No.", a.driver?.user?.phone || a.driver?.alt_mobile)}
      `}
      ${row('Project', a.project)}
      ${row('Location', a.location)}
      ${row('Division / Department', branch?.name || a.department)}
      ${row('Assignment Date', a.start_date)}
      ${row('Mileage at Assignment', a.start_mileage ? a.start_mileage + ' km' : '')}
      ${row('Fuel Limit', a.fuel_limit ? a.fuel_limit + ' L / month' : '')}
      ${row('Expected End Date', a.end_date)}
      ${row('Withdrawn On', a.status === 'completed' ? (a.end_date || '—') : '')}
      ${row('Purpose', a.purpose)}
    </table>

    <div class="terms">
      <strong>Terms &amp; Conditions</strong>
      <ol>
        <li>The vehicle is assigned for official duties only and must be used with due care.</li>
        <li>The assignee is responsible for the safe custody of the vehicle, its keys and documents.</li>
        <li>Fuel drawn shall not exceed the approved limit stated above without prior approval.</li>
        <li>Any accident, damage, breakdown or loss must be reported to the Transport Division immediately.</li>
        <li>The vehicle must be surrendered on the withdrawal date or on request by the institute.</li>
      </ol>
    </div>

    <table class="sign">
      <tr>
        <td><div class="line">${isOfficer ? (a.officer_name || 'Assignee') : (a.responsible_person || 'Responsible Person')}<br/>Assignee</div></td>
        <td><div class="line">Transport Officer</div></td>
        <td><div class="line">Head of Institute</div></td>
      </tr>
    </table>`;
};

export default function Assignments() {
  const { can } = useAuth();
  const [items,    setItems]    = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers,  setDrivers]  = useState([]);
  const [branches, setBranches] = useState([]);
  const [lists,    setLists]    = useState({});
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [modal,    setModal]    = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [filterStatus, setFilterStatus] = useState('');

  const emptyForm = { vehicle_id:'', driver_id:'', assign_type:'pool', officer_title:'Mr.', officer_name:'', officer_designation:'', officer_company:'', officer_employee_no:'', officer_nic:'', officer_address:'', project:'', location:'', fuel_limit:'', start_mileage:'', branch_id:'', department:'', notify_email:'', notify_phone:'', responsible_person:'', responsible_email:'', responsible_phone:'', purpose:'', start_date:'', end_date:'', notes:'' };
  const [form, setForm] = useState(emptyForm);

  // When a branch is picked: link it + prefill contact details (editable, notifications only)
  const onBranchChange = (e) => {
    const id = e.target.value;
    const b = branches.find(x => String(x.id) === id);
    setForm({
      ...form,
      branch_id: id,
      department: b ? b.name : '',          // keeps existing list display working
      notify_email: b?.email || '',
      notify_phone: b?.phone || '',
    });
  };

  const load = () => {
    setLoading(true);
    const safe = (p, fallback) => p.then(r => r).catch(e => ({ __err: e, data: { data: fallback } }));
    Promise.all([
      safe(assignmentAPI.getAll({ limit: 100 }), []),
      safe(vehicleAPI.getAll({ limit: 100 }), []),
      safe(driverAPI.getAll({ limit: 100 }), []),
      safe(api.get('/branches'), []),
      safe(lookupAPI.getAll(), {}),
    ])
      .then(([a, v, d, b, lk]) => {
        setLists(lk.data.data || {});
        setItems(a.data.data || []);
        setVehicles(v.data.data || []);
        setDrivers(d.data.data || []);
        setBranches((b.data.data || []).filter(x => x.status !== 'inactive'));
        const failed = [a, v, d].find(r => r.__err);
        if (failed) {
          setError(failed.__err?.response?.data?.message ||
            'Some data could not be loaded — the server may need updating (run npm run migrate and restart).');
        }
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // ── Only show vehicles that are NOT currently assigned (status !== 'assigned')
  // and NOT under repair
  // Only genuinely available: status 'active' (excludes assigned, under_repair,
  // inactive, sold, transferred) and not already in an active assignment.
  const activeAssignedIds = new Set(items.filter(a => a.status === 'active').map(a => a.vehicle_id));
  // Drivers already on an active assignment, and why each unavailable driver is out
  const busyDriverIds = new Set(items.filter(a => a.status === 'active' && a.driver_id).map(a => a.driver_id));
  const driverBlockedReason = (d) => {
    if (d.status && d.status !== 'active') return d.status;                    // suspended / resigned
    if (d.user && d.user.is_active === false) return 'account inactive';
    if (busyDriverIds.has(d.id)) {
      const a = items.find(x => x.status === 'active' && x.driver_id === d.id);
      return `assigned to ${a?.vehicle?.reg_number || 'another vehicle'}`;
    }
    return null;
  };
  const availableDrivers = drivers.filter(d => !driverBlockedReason(d));
  const blockedDrivers   = drivers.filter(d => driverBlockedReason(d));
  const availableVehicles = vehicles.filter(
    v => v.status === 'active' && !activeAssignedIds.has(v.id)
  );

  // ── Currently assigned vehicles (for info badge)
  const assignedIds = new Set(
    items.filter(a => a.status === 'active').map(a => a.vehicle_id)
  );

  const filtered = filterStatus
    ? items.filter(i => i.status === filterStatus)
    : items;

  const flash = (msg, isErr = false) => {
    isErr ? setError(msg) : setSuccess(msg);
    setTimeout(() => isErr ? setError('') : setSuccess(''), 3500);
  };

  const openAdd = () => { setForm(emptyForm); setModal(true); };

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await assignmentAPI.create(form);
      setModal(false);
      load();
      flash('Assignment created successfully');
    } catch (err) {
      flash(err.response?.data?.message || 'Failed to create assignment', true);
    } finally { setSaving(false); }
  };

  const endAssignment = async (id, vehicleReg) => {
    if (!window.confirm(`End assignment for vehicle ${vehicleReg}?`)) return;
    try {
      await assignmentAPI.endAssignment(id, {});
      load();
      flash(`Assignment for ${vehicleReg} ended`);
    } catch {
      flash('Failed to end assignment', true);
    }
  };

  const counts = {
    all:       items.length,
    active:    items.filter(i => i.status === 'active').length,
    completed: items.filter(i => i.status === 'completed').length,
    cancelled: items.filter(i => i.status === 'cancelled').length,
  };

  return (
    <div>
      <PageHeader
        title="Vehicle Assignments"
        sub={`${counts.active} active · ${counts.completed} completed`}
        action={can('admin','transport_officer') && (
          <Button onClick={openAdd}>+ New Assignment</Button>
        )}
      />

      {/* Status filter pills */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {[
          { label: `All (${counts.all})`,             val: '' },
          { label: `Active (${counts.active})`,        val: 'active' },
          { label: `Completed (${counts.completed})`,  val: 'completed' },
          { label: `Cancelled (${counts.cancelled})`,  val: 'cancelled' },
        ].map(f => (
          <button key={f.val} onClick={() => setFilterStatus(f.val)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all
              ${filterStatus === f.val
                ? 'bg-primary-500 text-white border-primary-500'
                : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {error   && <div className="mb-4"><Alert type="error"   message={error}   /></div>}
      {success && <div className="mb-4"><Alert type="success" message={success} /></div>}

      {loading ? <Loading /> : (
        <Table headers={['Vehicle','Driver','Department','Purpose','Start','End','Status','Actions']}>
          {filtered.map(a => (
            <tr key={a.id} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <span className="font-mono text-sm font-semibold text-primary-600">
                  {a.vehicle?.reg_number}
                </span>
                <p className="text-xs text-gray-400">{a.vehicle?.brand} {a.vehicle?.model}</p>
              </td>
              <td className="px-4 py-3 text-sm">{a.driver?.user?.name || '—'}</td>
              <td className="px-4 py-3 text-sm">
                {a.assign_type === 'officer'
                  ? <><span title="Assigned to officer">👤 {[a.officer_title, a.officer_name].filter(Boolean).join(' ') || 'Officer'}</span>
                      <p className="text-xs text-gray-400">{a.department || ''}</p></>
                  : <><span title="Pool vehicle">🏢 {a.department || 'Pool'}</span></>}
                {a.project && <p className="text-xs text-gray-400">{a.project}</p>}
              </td>
              <td className="px-4 py-3 text-sm max-w-[160px] truncate">{a.purpose || '—'}</td>
              <td className="px-4 py-3 text-xs text-gray-500">{a.start_date}</td>
              <td className="px-4 py-3 text-xs text-gray-500">
                {a.end_date && a.end_date !== '0000-00-00' ? a.end_date : (
                  <span className="text-green-600 font-medium">Ongoing</span>
                )}
              </td>
              <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
              <td className="px-4 py-3">
                <div className="flex gap-1 flex-wrap">
                  <Button variant="ghost" size="sm" title="Print assignment agreement"
                    onClick={() => printHTML(buildAgreement(a, branches))}>📄 Agreement</Button>
                  {can('admin','transport_officer') && a.status === 'active' && (
                    <Button variant="danger" size="sm"
                      onClick={() => endAssignment(a.id, a.vehicle?.reg_number)}>
                      End
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr>
              <td colSpan={8} className="px-4 py-10 text-center text-gray-400 text-sm">
                No assignments found
              </td>
            </tr>
          )}
        </Table>
      )}

      {/* ── New Assignment Modal ── */}
      <Modal open={modal} onClose={() => setModal(false)} title="New Vehicle Assignment" size="md">
        <form onSubmit={save} className="space-y-4">

          {/* Available vehicles only */}
          <div>
            <Select label="Vehicle *" value={form.vehicle_id}
              onChange={e => {
                const v = vehicles.find(x => String(x.id) === e.target.value);
                setForm({ ...form, vehicle_id: e.target.value, start_mileage: v?.odometer ?? '' });
              }} required>
              <option value="">Select vehicle…</option>
              {availableVehicles.map(v => (
                <option key={v.id} value={v.id}>
                  {v.reg_number} — {v.brand} {v.model} ({v.status})
                </option>
              ))}
            </Select>
            {form.vehicle_id && (() => {
              const v = vehicles.find(x => String(x.id) === String(form.vehicle_id));
              if (!v) return null;
              return (
                <div className="mt-2 bg-gray-50 border rounded-lg p-3 grid grid-cols-3 gap-2 text-xs">
                  {[['Make', v.brand], ['Model', v.model], ['YOM', v.year],
                    ['Engine No.', v.engine_number], ['Chassis No.', v.chassis_number],
                    ['Fuel Type', v.fuel_type], ['Odometer', v.odometer ? `${v.odometer} km` : null],
                    ['Type', v.type], ['Color', v.color]].map(([k, val]) => (
                    <div key={k}><span className="text-gray-400">{k}:</span> <span className="font-medium capitalize">{val || '—'}</span></div>
                  ))}
                </div>
              );
            })()}
            {availableVehicles.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">
                ⚠ All vehicles are currently assigned or under repair.
                End an existing assignment first.
              </p>
            )}
            {/* Info box: show how many are currently assigned */}
            {assignedIds.size > 0 && (
              <div className="mt-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <span className="font-medium text-amber-600">{assignedIds.size} vehicle(s)</span> currently
                assigned — hidden from this list. End their assignment to make them available.
              </div>
            )}
          </div>

          <Select label="Driver" value={form.driver_id}
            onChange={e => setForm({ ...form, driver_id: e.target.value })}>
            <option value="">Select driver…</option>
            {availableDrivers.map(d => (
              <option key={d.id} value={d.id}>
                {d.user?.name} — {d.license_number} ({d.license_type})
              </option>
            ))}
          </Select>
          {blockedDrivers.length > 0 && (
            <div className="-mt-2 text-xs text-gray-500 bg-gray-50 border rounded-lg p-2">
              <span className="font-medium text-amber-600">{blockedDrivers.length} driver(s) unavailable:</span>{' '}
              {blockedDrivers.map(d => `${d.user?.name} (${driverBlockedReason(d)})`).join(' · ')}
            </div>
          )}
          {availableDrivers.length === 0 && drivers.length > 0 && (
            <p className="-mt-2 text-xs text-amber-600">
              ⚠ No drivers are free — end an existing assignment, or reactivate a driver.
            </p>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Assigned To *</label>
            <div className="grid grid-cols-2 gap-2">
              {[['pool','🏢 Pool Vehicle','Branch / department pool'],['officer','👤 Officer','Allocated to a specific person']].map(([k, l, d]) => (
                <button type="button" key={k} onClick={() => setForm({ ...form, assign_type: k })}
                  className={`p-3 rounded-xl border-2 text-left ${form.assign_type === k ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                  <div className="text-sm font-medium">{l}</div>
                  <div className="text-xs text-gray-400">{d}</div>
                </button>
              ))}
            </div>
          </div>

          {form.assign_type === 'officer' && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide mb-3">👤 Officer Details</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid grid-cols-3 gap-2">
                  <Select label="Title" value={form.officer_title || 'Mr.'}
                    onChange={e => setForm({ ...form, officer_title: e.target.value })}>
                    <option value="Mr.">Mr.</option>
                    <option value="Mrs.">Mrs.</option>
                    <option value="Ms.">Ms.</option>
                    <option value="Dr.">Dr.</option>
                  </Select>
                  <div className="col-span-2">
                    <Input label="Name *" required value={form.officer_name}
                      onChange={e => setForm({ ...form, officer_name: e.target.value })} />
                  </div>
                </div>
                <Input label="Designation" value={form.officer_designation}
                  onChange={e => setForm({ ...form, officer_designation: e.target.value })} />
                <Input label="Company" value={form.officer_company}
                  onChange={e => setForm({ ...form, officer_company: e.target.value })} />
                <Input label="Employee No." value={form.officer_employee_no}
                  onChange={e => setForm({ ...form, officer_employee_no: e.target.value })} />
                <Input label="NIC" value={form.officer_nic}
                  onChange={e => setForm({ ...form, officer_nic: e.target.value })} />
                <Input label="Address" value={form.officer_address}
                  onChange={e => setForm({ ...form, officer_address: e.target.value })} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Select label="Project" value={form.project}
              onChange={e => setForm({ ...form, project: e.target.value })}>
              <option value="">Select project…</option>
              {(lists.project || []).map(p => <option key={p} value={p}>{p}</option>)}
              {form.project && !(lists.project || []).includes(form.project) && (
                <option value={form.project}>{form.project}</option>
              )}
            </Select>
            <Select label="Location" value={form.location}
              onChange={e => setForm({ ...form, location: e.target.value })}>
              <option value="">Select location…</option>
              {(lists.location || []).map(l => <option key={l} value={l}>{l}</option>)}
              {form.location && !(lists.location || []).includes(form.location) && (
                <option value={form.location}>{form.location}</option>
              )}
            </Select>
            <Input label="Fuel Limit (Litres / month)" type="number" step="0.01" value={form.fuel_limit}
              onChange={e => setForm({ ...form, fuel_limit: e.target.value })} />
            <Input label="Mileage at Assignment (km)" type="number" value={form.start_mileage}
              onChange={e => setForm({ ...form, start_mileage: e.target.value })} placeholder="auto-filled from vehicle" />
          </div>

          <Select label="Division / Department (Branch)" value={form.branch_id} onChange={onBranchChange}>
            <option value="">Select branch...</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>{b.name}{b.code ? ` (${b.code})` : ''}</option>
            ))}
          </Select>

          {form.branch_id && (
            <div className="grid grid-cols-2 gap-4">
              <Input label="Notification Email" type="email"
                value={form.notify_email}
                onChange={e => setForm({ ...form, notify_email: e.target.value })}
                placeholder="branch@hit.lk" />
              <Input label="Notification Phone"
                value={form.notify_phone}
                onChange={e => setForm({ ...form, notify_phone: e.target.value })}
                placeholder="011 XXXXXXX" />
              <p className="col-span-2 -mt-2 text-xs text-gray-500">
                Loaded from the branch — change here for this notification only (branch record is not modified).
              </p>
              {form.assign_type === 'pool' && (<>
              <div className="col-span-2 pt-1 border-t">
                <p className="text-sm font-medium text-gray-700 mt-2 mb-2">Responsible Person (in this branch)</p>
              </div>
              <Input label="Name"
                value={form.responsible_person}
                onChange={e => setForm({ ...form, responsible_person: e.target.value })}
                placeholder="e.g. K. Silva" />
              <Input label="Mobile No"
                value={form.responsible_phone}
                onChange={e => setForm({ ...form, responsible_phone: e.target.value })}
                placeholder="07X XXXXXXX" />
              <div className="col-span-2">
                <Input label="Email" type="email"
                  value={form.responsible_email}
                  onChange={e => setForm({ ...form, responsible_email: e.target.value })}
                  placeholder="person@hit.lk" />
                <p className="text-xs text-gray-500 mt-1">The responsible person also receives the assignment notification email.</p>
              </div>
              </>)}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
            <textarea value={form.purpose}
              onChange={e => setForm({ ...form, purpose: e.target.value })}
              placeholder="e.g. Site operations — Skyline Residences project"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none
                         focus:ring-2 focus:ring-primary-300 h-20 resize-none" />
          </div>

          <Input label="Start Date *" type="date" value={form.start_date}
            onChange={e => setForm({ ...form, start_date: e.target.value })} required />

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || availableVehicles.length === 0}>
              {saving ? 'Assigning…' : 'Assign Vehicle'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
